import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Home, Lock, User, AlertTriangle, Eye, EyeOff } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Password change modal states
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [newPasswordData, setNewPasswordData] = useState({
    oldPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [changingPassword, setChangingPassword] = useState(false);
  const [tempToken, setTempToken] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/login`, formData);
      const { access_token } = response.data;
      
      // Store token temporarily
      setTempToken(access_token);
      
      // Verify and check if must change password
      const verifyResponse = await axios.get(`${API}/auth/verify`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      
      if (verifyResponse.data.must_change_password) {
        // Show password change modal
        setNewPasswordData({ ...newPasswordData, oldPassword: formData.password });
        setShowPasswordChange(true);
        toast.warning('Vous devez changer votre mot de passe par défaut pour continuer');
      } else {
        // Normal login
        localStorage.setItem('admin_token', access_token);
        toast.success('Connexion réussie!');
        navigate('/admin');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur de connexion');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (newPasswordData.newPassword !== newPasswordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (newPasswordData.newPassword.length < 8) {
      toast.error('Le nouveau mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    if (newPasswordData.newPassword === newPasswordData.oldPassword) {
      toast.error('Le nouveau mot de passe doit être différent de l\'ancien');
      return;
    }
    
    setChangingPassword(true);
    
    try {
      await axios.post(`${API}/auth/change-password`, {
        old_password: newPasswordData.oldPassword,
        new_password: newPasswordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${tempToken}` }
      });
      
      // Store token and navigate
      localStorage.setItem('admin_token', tempToken);
      toast.success('Mot de passe changé avec succès!');
      navigate('/admin');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4" style={{ background: 'linear-gradient(135deg, #FAFAFA 0%, #F0EDE6 50%, #E8E4D9 100%)' }}>
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <button
            onClick={() => navigate('/')}
            className="inline-flex items-center text-sm mb-6 hover:underline"
            style={{ color: '#335C2E' }}
            data-testid="back-home-btn"
          >
            <Home className="w-4 h-4 mr-1" />
            Retour à l'accueil
          </button>
          <h1 className="text-4xl font-bold mb-2" style={{ color: '#335C2E' }}>
            Connexion Admin
          </h1>
          <p style={{ color: '#666666' }}>Espace administrateur Refacing VR</p>
        </div>

        {/* Password Change Modal */}
        {showPasswordChange && (
          <div className="card mb-6 border-l-4" style={{ borderColor: '#FF9800', background: '#FFF8E1' }}>
            <div className="flex items-start gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 flex-shrink-0" style={{ color: '#FF9800' }} />
              <div>
                <h2 className="font-bold text-lg" style={{ color: '#333' }}>
                  Changement de mot de passe requis
                </h2>
                <p className="text-sm" style={{ color: '#666' }}>
                  Pour des raisons de sécurité, veuillez définir un nouveau mot de passe.
                </p>
              </div>
            </div>
            
            <form onSubmit={handlePasswordChange} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#666666' }}>
                  Nouveau mot de passe
                </label>
                <div className="relative">
                  <span 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center pointer-events-none"
                    style={{ color: '#999999', width: '16px', height: '16px' }}
                  >
                    <Lock className="w-4 h-4" style={{ display: 'block', stroke: 'currentColor', fill: 'none' }} />
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pl-10 pr-10"
                    value={newPasswordData.newPassword}
                    onChange={(e) => setNewPasswordData({ ...newPasswordData, newPassword: e.target.value })}
                    placeholder="Minimum 8 caractères"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center"
                    style={{ color: '#999999' }}
                  >
                    {showPassword ? 
                      <EyeOff className="w-4 h-4" style={{ display: 'block', stroke: 'currentColor', fill: 'none' }} /> : 
                      <Eye className="w-4 h-4" style={{ display: 'block', stroke: 'currentColor', fill: 'none' }} />
                    }
                  </button>
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-1" style={{ color: '#666666' }}>
                  Confirmer le nouveau mot de passe
                </label>
                <div className="relative">
                  <span 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 flex items-center justify-center pointer-events-none"
                    style={{ color: '#999999', width: '16px', height: '16px' }}
                  >
                    <Lock className="w-4 h-4" style={{ display: 'block', stroke: 'currentColor', fill: 'none' }} />
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pl-10"
                    value={newPasswordData.confirmPassword}
                    onChange={(e) => setNewPasswordData({ ...newPasswordData, confirmPassword: e.target.value })}
                    placeholder="Répétez le mot de passe"
                    required
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={changingPassword}
                className="btn-primary w-full"
              >
                {changingPassword ? 'Changement...' : 'Changer le mot de passe'}
              </button>
            </form>
          </div>
        )}

        {/* Login Form */}
        {!showPasswordChange && (
          <div className="card">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>
                  Nom d'utilisateur
                </label>
                <div className="relative">
                  <span 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center justify-center pointer-events-none"
                    style={{ color: '#999999', width: '20px', height: '20px' }}
                  >
                    <User className="w-5 h-5" style={{ display: 'block', stroke: 'currentColor', fill: 'none' }} />
                  </span>
                  <input
                    type="text"
                    className="input-field pl-12"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    placeholder="admin"
                    required
                    data-testid="username-input"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2" style={{ color: '#666666' }}>
                  Mot de passe
                </label>
                <div className="relative">
                  <span 
                    className="absolute left-4 top-1/2 transform -translate-y-1/2 flex items-center justify-center pointer-events-none"
                    style={{ color: '#999999', width: '20px', height: '20px' }}
                  >
                    <Lock className="w-5 h-5" style={{ display: 'block', stroke: 'currentColor', fill: 'none' }} />
                  </span>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input-field pl-12 pr-12"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="••••••••"
                    required
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-4 top-1/2 transform -translate-y-1/2 flex items-center justify-center"
                    style={{ color: '#999999' }}
                  >
                    {showPassword ? 
                      <EyeOff className="w-5 h-5" style={{ display: 'block', stroke: 'currentColor', fill: 'none' }} /> : 
                      <Eye className="w-5 h-5" style={{ display: 'block', stroke: 'currentColor', fill: 'none' }} />
                    }
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
                data-testid="login-submit-btn"
              >
                {loading ? 'Connexion...' : 'Se connecter'}
              </button>
            </form>

            <div className="mt-6 p-4 rounded-lg" style={{ background: '#F5F5F5' }}>
              <p className="text-xs" style={{ color: '#666666' }}>
                <strong>Note:</strong> Lors de la première connexion avec le mot de passe par défaut, 
                vous serez invité à le changer pour des raisons de sécurité.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Login;
